import { useEffect, useState, useCallback } from "react";
import { api, formatRupiah } from "@/lib/api";
import { PageHeader, Badge, EmptyState } from "@/components/common";
import { toast } from "sonner";
import { HandCoins, Users, Coins, CheckCircle2, Circle, Filter } from "lucide-react";

const KAT_CLS = {
  makan: "bg-orange-500/10 text-orange-300 border-orange-500/30",
  transport: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  akomodasi: "bg-purple-500/10 text-purple-300 border-purple-500/30",
  lain: "bg-slate-500/10 text-slate-300 border-slate-500/30",
};

const inputCls =
  "px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm focus:outline-none focus:border-indigo-500 transition";

export default function ReimburseSummary() {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [eventId, setEventId] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    if (eventId !== "all") params.event_id = eventId;
    const { data } = await api.get("/reimbursements/summary", { params });
    setData(data);
  }, [start, end, eventId]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const togglePaid = async (item) => {
    const next = item.status_bayar === "lunas" ? "belum" : "lunas";
    await api.put(`/reimbursements/${item.id}/status`, null, { params: { status_bayar: next } });
    toast.success(next === "lunas" ? "Ditandai sudah ditransfer" : "Ditandai belum dibayar");
    load();
  };

  if (!data) return <div className="flex items-center justify-center h-96 text-slate-500 font-mono text-sm">Memuat...</div>;

  return (
    <div>
      <PageHeader title="Rekap Reimburse" subtitle="Pembayaran ke Staff" />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 text-slate-500"><Filter className="w-4 h-4" /><span className="text-xs font-mono uppercase tracking-wider">Filter</span></div>
        <div className="flex items-center gap-2">
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} data-testid="reimburse-filter-start" />
          <span className="text-slate-500 text-sm">s/d</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls} data-testid="reimburse-filter-end" />
        </div>
        <select value={eventId} onChange={(e) => setEventId(e.target.value)} className={inputCls} data-testid="reimburse-filter-event">
          <option value="all">Semua Event</option>
          {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.nama_event}</option>)}
        </select>
        {(start || end || eventId !== "all") && (
          <button onClick={() => { setStart(""); setEnd(""); setEventId("all"); }} className="text-xs text-indigo-400 hover:text-indigo-300">Reset</button>
        )}
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-xl border border-slate-800 bg-[#131822] p-5">
          <div className="flex items-center gap-2 mb-2"><Coins className="w-4 h-4 text-amber-400" /><p className="text-xs font-mono uppercase text-slate-500">Total Reimburse</p></div>
          <p className="font-mono text-2xl font-black text-amber-300" data-testid="reimburse-total-all">{formatRupiah(data.total_all)}</p>
        </div>
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-5">
          <div className="flex items-center gap-2 mb-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /><p className="text-xs font-mono uppercase text-slate-500">Sudah Ditransfer</p></div>
          <p className="font-mono text-2xl font-black text-emerald-300" data-testid="reimburse-total-lunas">{formatRupiah(data.total_lunas)}</p>
        </div>
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-5">
          <div className="flex items-center gap-2 mb-2"><Circle className="w-4 h-4 text-rose-400" /><p className="text-xs font-mono uppercase text-slate-500">Belum Dibayar</p></div>
          <p className="font-mono text-2xl font-black text-rose-300" data-testid="reimburse-total-belum">{formatRupiah(data.total_belum)}</p>
        </div>
      </div>

      {data.per_penerima.length === 0 ? (
        <EmptyState icon={HandCoins} title="Belum ada reimburse" subtitle="Reimburse yang dicatat di tiap event akan direkap di sini per staff." />
      ) : (
        <div className="space-y-4" data-testid="reimburse-summary-list">
          {data.per_penerima.map((p) => (
            <div key={p.penerima} className="rounded-xl border border-slate-800 bg-[#131822] overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-800 bg-slate-900/40">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-sm font-bold text-indigo-300 uppercase">{p.penerima[0]}</div>
                  <div>
                    <p className="font-heading font-semibold text-white">{p.penerima}</p>
                    <p className="text-xs text-slate-500">{p.bank || "Bank -"} • {p.nomor_rekening || "rekening belum diisi"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-5 text-right">
                  <div><p className="text-[10px] font-mono uppercase text-slate-500">Belum</p><p className="font-mono font-bold text-rose-300">{formatRupiah(p.total_belum)}</p></div>
                  <div><p className="text-[10px] font-mono uppercase text-slate-500">Lunas</p><p className="font-mono font-bold text-emerald-300">{formatRupiah(p.total_lunas)}</p></div>
                  <div><p className="text-[10px] font-mono uppercase text-slate-500">Total</p><p className="font-mono font-black text-amber-300">{formatRupiah(p.total)}</p></div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                      <th className="text-left px-5 py-2.5">Tanggal</th>
                      <th className="text-left px-5 py-2.5">Event</th>
                      <th className="text-left px-5 py-2.5">Kategori</th>
                      <th className="text-left px-5 py-2.5">Catatan</th>
                      <th className="text-right px-5 py-2.5">Jumlah</th>
                      <th className="text-center px-5 py-2.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {p.items.map((it) => (
                      <tr key={it.id} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition">
                        <td className="px-5 py-2.5 text-slate-400 font-mono text-xs whitespace-nowrap">{it.created_at ? new Date(it.created_at).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "2-digit" }) : "-"}</td>
                        <td className="px-5 py-2.5"><span className="text-white">{it.event_nama}</span></td>
                        <td className="px-5 py-2.5"><Badge className={KAT_CLS[it.kategori] || KAT_CLS.lain}><span className="capitalize">{it.kategori}</span></Badge></td>
                        <td className="px-5 py-2.5 text-slate-400">{it.catatan || "-"}</td>
                        <td className="px-5 py-2.5 text-right font-mono text-rose-300">{formatRupiah(it.jumlah)}</td>
                        <td className="px-5 py-2.5 text-center">
                          <button
                            onClick={() => togglePaid(it)}
                            data-testid={`toggle-paid-${it.id}`}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                              it.status_bayar === "lunas"
                                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/20"
                                : "bg-rose-500/10 text-rose-300 border-rose-500/30 hover:bg-rose-500/20"
                            }`}
                          >
                            {it.status_bayar === "lunas" ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                            {it.status_bayar === "lunas" ? "Sudah ditransfer" : "Tandai lunas"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
